import React from 'react';
import { Shield, Lock, Server, Eye, FileCheck, Globe, CheckCircle, Users, Database, Key, AlertTriangle, Award } from 'lucide-react';

const Security = () => {
  const securityFeatures = [
    {
      icon: <Server className="w-8 h-8 text-[#854DFF]" />,
      title: 'AWS Infrastructure',
      description: 'Enterprise-grade hosting on Amazon Web Services with 99.99% uptime SLA and multi-region redundancy for maximum reliability and performance.'
    },
    {
      icon: <Lock className="w-8 h-8 text-[#854DFF]" />,
      title: 'End-to-End Encryption',
      description: 'All data is encrypted at rest using AES-256 encryption and in transit using TLS 1.3, ensuring your sensitive information remains secure.'
    },
    {
      icon: <Database className="w-8 h-8 text-[#854DFF]" />,
      title: 'Data Isolation',
      description: 'Complete data isolation between partners and tenants with dedicated database instances and network segmentation to prevent unauthorized access.'
    },
    {
      icon: <Shield className="w-8 h-8 text-[#854DFF]" />,
      title: 'SOC 2 Type II Certified',
      description: 'Independently audited and certified for security, availability, processing integrity, confidentiality, and privacy controls.'
    },
    {
      icon: <Globe className="w-8 h-8 text-[#854DFF]" />,
      title: 'GDPR Compliant',
      description: 'Full compliance with EU General Data Protection Regulation, including data portability, right to be forgotten, and consent management.'
    },
    {
      icon: <FileCheck className="w-8 h-8 text-[#854DFF]" />,
      title: 'ISO 27001 Certified',
      description: 'International standard for information security management systems, demonstrating our commitment to protecting your data.'
    },
    {
      icon: <Key className="w-8 h-8 text-[#854DFF]" />,
      title: 'Multi-Factor Authentication',
      description: 'Mandatory MFA for all user accounts with support for authenticator apps, SMS, and hardware security keys.'
    },
    {
      icon: <Eye className="w-8 h-8 text-[#854DFF]" />,
      title: 'Continuous Monitoring',
      description: '24/7 security monitoring with real-time threat detection, automated incident response, and comprehensive audit logging.'
    },
    {
      icon: <Users className="w-8 h-8 text-[#854DFF]" />,
      title: 'Role-Based Access Control',
      description: 'Granular permission management with least-privilege access principles and customizable role hierarchies.'
    }
  ];

  const complianceStandards = [
    {
      name: 'SOC 2 Type II',
      description: 'Annual audit of security, availability, and confidentiality controls',
      icon: <Award className="w-6 h-6" />
    },
    {
      name: 'GDPR',
      description: 'Full compliance with EU data protection regulations',
      icon: <Globe className="w-6 h-6" />
    },
    {
      name: 'ISO 27001',
      description: 'Information security management system certification',
      icon: <FileCheck className="w-6 h-6" />
    },
    {
      name: 'CCPA',
      description: 'California Consumer Privacy Act compliance',
      icon: <Shield className="w-6 h-6" />
    },
    {
      name: 'PCI DSS',
      description: 'Payment Card Industry Data Security Standard',
      icon: <Lock className="w-6 h-6" />
    },
    {
      name: 'HIPAA',
      description: 'Healthcare data protection and privacy standards',
      icon: <FileCheck className="w-6 h-6" />
    }
  ];

  const securityPractices = [
    {
      category: 'Data Protection',
      practices: [
        'AES-256 encryption at rest for all stored data',
        'TLS 1.3 encryption for data in transit',
        'Regular automated backups with point-in-time recovery',
        'Immutable backup storage with 30-day retention',
        'Data residency options for regional compliance'
      ]
    },
    {
      category: 'Network Security',
      practices: [
        'Web Application Firewall (WAF) with DDoS protection',
        'Private VPC with network segmentation',
        'Intrusion Detection and Prevention Systems (IDS/IPS)',
        'IP whitelisting and geo-blocking capabilities',
        'Secure API gateways with rate limiting'
      ]
    },
    {
      category: 'Access Management',
      practices: [
        'Single Sign-On (SSO) with SAML 2.0 support',
        'Multi-factor authentication (MFA) enforcement',
        'Session management with automatic timeout',
        'API key rotation and lifecycle management',
        'Comprehensive audit logs for all access events'
      ]
    },
    {
      category: 'Application Security',
      practices: [
        'Regular penetration testing by third-party security firms',
        'Automated vulnerability scanning and remediation',
        'Secure Software Development Lifecycle (SSDLC)',
        'Code review and static analysis for all releases',
        'Dependency scanning and patch management'
      ]
    },
    {
      category: 'Incident Response',
      practices: [
        '24/7 Security Operations Center (SOC)',
        'Automated threat detection and response',
        'Incident response plan with defined escalation procedures',
        'Regular disaster recovery drills and testing',
        'Transparent breach notification within 72 hours'
      ]
    },
    {
      category: 'Compliance & Auditing',
      practices: [
        'Annual third-party security audits',
        'Continuous compliance monitoring and reporting',
        'Data processing agreements (DPA) available',
        'Regular employee security training and awareness',
        'Vendor security assessment program'
      ]
    }
  ];

  const dataPrivacyCommitments = [
    {
      title: 'Data Ownership',
      description: 'You retain full ownership of your data. We never sell or share your data with third parties for marketing purposes.'
    },
    {
      title: 'Data Portability',
      description: 'Export your data at any time in standard formats. No lock-in, complete control over your information.'
    },
    {
      title: 'Right to Deletion',
      description: 'Request deletion of your data at any time. We comply with all data subject rights under GDPR and CCPA.'
    },
    {
      title: 'Minimal Data Collection',
      description: 'We only collect data necessary for service delivery and never retain data longer than required.'
    },
    {
      title: 'Transparent Processing',
      description: 'Clear documentation of how we process your data, who has access, and for what purposes.'
    },
    {
      title: 'Third-Party Audits',
      description: 'Regular independent security audits and certifications to validate our security claims.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <Shield className="w-20 h-20" />
            </div>
            <h1 className="text-5xl font-bold mb-6">
              Enterprise-Grade Security & Compliance
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Your data security is our top priority. CredFlexi employs industry-leading security measures, 
              comprehensive compliance certifications, and transparent practices to protect your business.
            </p>
          </div>
        </div>
      </section>

      {/* Key Security Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built on Security-First Principles
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive security measures across infrastructure, application, and data layers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Certifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Compliance & Certifications
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Independently audited and certified to meet the highest industry standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceStandards.map((standard, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg border-2 border-[#854DFF]/20 hover:border-[#854DFF] transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="text-[#854DFF] mt-1">
                    {standard.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {standard.name}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {standard.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Practices */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Security Practices
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multi-layered security approach protecting every aspect of your data
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {securityPractices.map((section, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <CheckCircle className="w-6 h-6 text-[#854DFF] mr-3" />
                  {section.category}
                </h3>
                <ul className="space-y-3">
                  {section.practices.map((practice, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{practice}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Privacy Commitments */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Privacy Commitments
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent data practices that respect your privacy and give you control
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dataPrivacyCommitments.map((commitment, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {commitment.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {commitment.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Infrastructure Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              AWS-Powered Infrastructure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on Amazon Web Services for unmatched reliability and security
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-10 border border-gray-100">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Infrastructure Highlights</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Multi-region deployment with automatic failover</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">AWS VPC for network isolation and security</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">AWS Shield for DDoS protection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">AWS KMS for encryption key management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">AWS CloudTrail for comprehensive audit logging</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">AWS WAF for web application protection</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Data Protection</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <Lock className="w-5 h-5 text-[#854DFF] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">AES-256 encryption for all data at rest</span>
                  </li>
                  <li className="flex items-start">
                    <Lock className="w-5 h-5 text-[#854DFF] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">TLS 1.3 for all data in transit</span>
                  </li>
                  <li className="flex items-start">
                    <Database className="w-5 h-5 text-[#854DFF] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Dedicated database instances per tenant</span>
                  </li>
                  <li className="flex items-start">
                    <Database className="w-5 h-5 text-[#854DFF] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Complete data isolation between partners</span>
                  </li>
                  <li className="flex items-start">
                    <Server className="w-5 h-5 text-[#854DFF] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Automated daily backups with 30-day retention</span>
                  </li>
                  <li className="flex items-start">
                    <Server className="w-5 h-5 text-[#854DFF] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Point-in-time recovery for data protection</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Contact */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] rounded-2xl p-12 text-center text-white">
            <AlertTriangle className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              Report a Security Vulnerability
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              We take security seriously. If you discover a security vulnerability, please report it to our security team immediately.
            </p>
            <div className="space-y-3">
              <p className="text-lg">
                <strong>Email:</strong> security@credflexi.com
              </p>
              <p className="text-lg">
                <strong>Response Time:</strong> Within 24 hours
              </p>
            </div>
            <p className="mt-6 text-sm text-white/80">
              We appreciate responsible disclosure and will work with you to address any security concerns.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Center CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Want More Details?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Access our comprehensive security documentation, audit reports, and compliance certificates.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-[#7A42E6] hover:to-[#6F37CC] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Request Security Documentation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Security;
